import { createApp } from '@backstage/frontend-defaults';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import azureDevopsPlugin from '@backstage-community/plugin-azure-devops/alpha';
import sonarqubePlugin from '@backstage-community/plugin-sonarqube/alpha';
import rbacPlugin from '@backstage-community/plugin-rbac/alpha';
import { navModule } from './modules/nav';
import { homeModule } from './modules/home';
import {
  SignInPageBlueprint,
  SwappableComponentBlueprint,
} from '@backstage/plugin-app-react';
import {
  createFrontendModule,
  NotFoundErrorPage,
} from '@backstage/frontend-plugin-api';
import { themeModule } from './modules/theme';
import { authModule } from './modules/auth';
import { kindsModule } from './modules/kinds';
import {
  permissionsModule,
  rbacHeaderBridgeModule,
} from './modules/permissions';
import { techdocsModule } from './modules/techdocs';
import { catalogModule } from './modules/catalog';
import { apiDocsModule } from './modules/apiDocs';
import { scaffolderModule, scaffolderTemplatesModule } from './modules/scaffolder';
import { seedsModule } from './modules/seeds';
import { contentPagesModule } from './modules/contentPages';
import { iframePreviewModule } from './modules/iframePreview';
import { notificationsAdminModule } from './modules/notificationsAdmin';
import { governanceModule } from './modules/governance';
import { SignInPage } from './components/auth/SignInPage';

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => <SignInPage {...props} />,
  },
});

/*
 * Swap the upstream `core-not-found-error-page` component (which renders
 * the teal "Looks like someone dropped the mic!" page from
 * `@backstage/core-components`) for our Bosch-branded one — blue blobs
 * matching the brand guide + a `mailto:` link straight to the team's
 * support inbox. The actual UI lives in
 * `packages/app/src/components/errorPage/NotFoundPage.tsx`; the long
 * comment at the top of that file explains why we re-implemented the
 * whole page instead of just swapping the SVG asset.
 *
 * Why the registration is here in App.tsx instead of in
 * `packages/app/src/modules/errorPage/`:
 *   The `SwappableComponentsApi` factory (see
 *   `node_modules/@backstage/plugin-app/dist/extensions/SwappableComponentsApi.esm.js`)
 *   silently drops every component override whose
 *   `i.node.spec.plugin?.id` is not `"app"`, with a console.warn:
 *
 *     "SwappableComponents should only be installed as an extension in
 *      the app plugin. You can either use appPlugin.override(), or
 *      provide a module for the app-plugin with the extension there
 *      instead."
 *
 *   So the override MUST live inside a `createFrontendModule({ pluginId:
 *   'app', ... })` block — which is exactly the inline module already
 *   used below for the sign-in page. We piggy-back on it instead of
 *   spawning a parallel `errorPageModule` file just to follow that one
 *   rule.
 *
 * Mechanism (see `DefaultSwappableComponentsApi.fromComponents`):
 *   Components are stored in a `Map` keyed by `ref.id`. Both the
 *   default `NotFoundErrorPage` extension and this override use the
 *   same `ref` (imported from `@backstage/frontend-plugin-api`), so
 *   they share the key `"core-not-found-error-page"`. Whichever
 *   extension is processed LAST wins the Map slot — and user-supplied
 *   `pluginId: 'app'` extensions come after the bundled default.
 *
 * The `name: 'bosch-not-found'` on the blueprint is just the extension
 * identity (`component:app/bosch-not-found`); it doesn't have to match
 * the ref id and choosing a distinct name avoids a duplicate-extension
 * error against the upstream `component:app/core-not-found-error-page`.
 */
const notFoundErrorPageOverride = SwappableComponentBlueprint.make({
  name: 'bosch-not-found',
  params: define =>
    define({
      component: NotFoundErrorPage,
      loader: async () => {
        const { NotFoundPage } = await import(
          './components/errorPage/NotFoundPage'
        );
        return () => <NotFoundPage />;
      },
    }),
});

export default createApp({
  features: [
    themeModule,
    homeModule,
    catalogPlugin,
    techdocsPlugin,
    sonarqubePlugin,
    azureDevopsPlugin,
    rbacPlugin,
    navModule,
    authModule,
    kindsModule,
    permissionsModule,
    rbacHeaderBridgeModule,
    techdocsModule,
    catalogModule,
    apiDocsModule,
    scaffolderTemplatesModule,
    scaffolderModule,
    seedsModule,
    contentPagesModule,
    iframePreviewModule,
    notificationsAdminModule,
    governanceModule,
    createFrontendModule({
      pluginId: 'app',
      extensions: [signInPage, notFoundErrorPageOverride],
    }),
  ],
});
